// Shape of the stats engine output (parser/src/jano_parser/stats.py compute()).
// The API will return exactly this shape, so the UI is unchanged when we swap to live data.

export interface PlayerStat {
  steamid64: string
  name: string
  team: string
  kills: number
  deaths: number
  assists: number
  kd: number
  plus_minus: number
  adr: number
  kast: number
  hs_pct: number
  opening_kills: number
  opening_deaths: number
  trade_kills: number
  traded_deaths: number
  untraded_deaths: number
  multi_kills: Record<string, number>
  rating: number
}

export interface RoundInfo {
  round: number
  winner_side: 'CT' | 'T'
  winner_team: string | null
  reason: string
  bomb_planted: boolean
}

export interface MatchData {
  map: string
  rounds: number
  team_score: Record<string, number>
  scoreboard: PlayerStat[]
  round_timeline: RoundInfo[]
}

// --- 2D replay (parser/scratchpad gen_replay.py output) ---

export interface ReplayPlayer {
  sid: string
  name: string
  team: string
  color: string
}

export interface ReplayKill {
  tf: number // frame index the death occurred at
  x: number
  y: number
  lo?: number // 1 = lower floor (absent on replays parsed before floors existed)
  victim: string
  attacker: string
  weapon: string
  hs: boolean
}

export interface Smoke {
  s: number // start frame
  e: number // end frame
  x: number
  y: number
  lo?: number // 1 = lower floor of a two-storey map (absent on replays parsed before floors existed)
}
export interface Pop {
  tf: number
  x: number
  y: number
  lo?: number
}

/** One grenade's flight: where it was released, the arc, and who threw it. `pts` is [x, y, lower]
 *  per replay frame from `s` to `e`. Absent on replays parsed before arcs were captured. */
export interface Throw {
  t: 'smokes' | 'molly' | 'he' | 'flashes'
  s: number
  e: number
  by: string
  pts: [number, number, number][]
}

export interface ReplayRound {
  round: number
  winnerTeam: string | null
  /** Empty on a split replay until the round's blob has been fetched — see ReplayData.split. */
  frames: number[][] // each: [x,y,yaw,alive,hp,money,weaponIdx,armor,helmet,defuser,lower] per player, in players[] order
  inv: number[][][] // inv[frame][playerIdx] = weapon indices held (loadout minus knife)
  kills: ReplayKill[]
  smokes: Smoke[]
  fires: Smoke[]
  flashes: Pop[]
  hes: Pop[]
  bomb?: { tf: number; site: string | null; lo?: number }
  throws?: Throw[]
}

/** Values per player per frame in replays parsed before the floor field existed. Current replays
 *  carry their own `stride` — read that, never this, or every position lands in the wrong slot. */
export const LEGACY_FRAME_STRIDE = 10

export interface ReplayData {
  map: string
  tickRate: number
  fps: number
  transform: { posX: number; posY: number; scale: number; size: number }
  ourTeam: string
  players: ReplayPlayer[]
  weapons: string[] // weaponIdx → name
  walkspace: [number, number][]
  rounds: ReplayRound[]
  /** Absent on replays parsed before floors existed — those are LEGACY_FRAME_STRIDE. */
  stride?: number
  /** True on two-storey maps (nuke), where the frame's last slot is the lower-floor flag. */
  layered?: boolean
  /** Frames and inventories live in one blob per round rather than inline. Absent on every demo
   *  parsed before 5 Aug, which is exactly what "absent" has to mean: frames are inline. */
  split?: boolean
}

// --- Deep stats (scratchpad gen_deepstats.py output) ---
export interface WeaponBreakdown {
  byWeapon: Record<string, { kills: number; hs: number }>
  shots: number
  hits: number
  acc: number
}
export interface OpeningStat {
  CT: { k: number; d: number }
  T: { k: number; d: number }
}
export type ClutchStat = Record<string, { w: number; p: number }> // "1v1".."1v5"
export interface UtilityStat {
  flashes: number
  smokes: number
  he: number
  molly: number
  enemiesFlashed: number
  blindTime: number
  teamFlashed: number
  flashAssists: number
  heDmg: number
  fireDmg: number
}
export interface EconRound {
  round: number
  buys: Record<string, number>
  types: Record<string, string>
  winner: string | null
}
export interface DeepStats {
  map: string
  rounds: number
  teams: string[]
  players: Record<string, { name: string; team: string }>
  weapons: Record<string, WeaponBreakdown>
  opening: Record<string, OpeningStat>
  clutches: Record<string, ClutchStat>
  utility: Record<string, UtilityStat>
  economy: { rounds: EconRound[]; byType: Record<string, Record<string, { w: number; p: number }>> }
  events: { type: string; x: number; y: number; round: number; hs: boolean; atkTeam: string; layer?: string | null }[]
  timeouts?: TimeoutStats
}

// --- Timeout analytics (deep.timeouts) ---
export interface TimeoutEvent {
  beforeRound: number
  gapSec: number
  streakTeam: string | null
  streakLen: number
  calledBy: string | null
  callSource: 'chat' | 'inferred' | 'unknown'
  nextRoundWinner: string | null
  next3: Record<string, number>
  brokeStreak: boolean
}
export interface TimeoutStats {
  events: TimeoutEvent[]
  byTeam: Record<
    string,
    {
      afterTimeoutWon: number
      afterTimeoutPlayed: number
      called: number
      brokeStreak: number
      next3Won: number
      next3Played: number
    }
  >
  baselineGapSec: number
  timeoutSec: number
}

// --- Opponent scouting (api/src/shared/scouting.ts) ---
export interface DemoRef {
  demoId: string
  map: string
  scoutedTeam: string
  addedAt: string
}
export interface Opponent {
  id: string
  name: string
  createdAt: string
  demos: DemoRef[]
  /** The FACEIT team behind this folder, when one is linked. Carried into the view because it is
   *  what makes their match history reachable — a folder without it can only ever hold the demos
   *  somebody uploaded by hand. */
  faceitTeamId?: string
  esplayTeamId?: string
}
/** One of their games, found by walking the roster's history — any tournament, not just ours. */
export interface TeamGame {
  matchId: string
  competition: string
  finishedAt: number
  teams: string[]
  maps: string[]
  /** FACEIT has published a demo for it. */
  demo: boolean
  /** How many of the roster played in it. */
  roster: number
}
export interface OpponentSummary {
  id: string
  name: string
  demoCount: number
  maps: string[]
  league?: string // season label for auto-created division folders
  faceitTeamId?: string
}
export interface DemoDiffChange {
  kind: 'lineup' | 'sites' | 'utility' | 'pace' | 'buys' | 'result'
  label: string
  before: string
  after: string
  direction: 'up' | 'down' | 'same'
  note?: string
}
export interface DemoDiff {
  from: string
  to: string
  sameMap: boolean
  changes: DemoDiffChange[]
}
export interface PlanItem {
  title: string
  evidence: string
  drill: string
  source: 'ai' | 'repeated' | 'map' | 'timeout' | 'economy'
  thin?: boolean
}
export interface PracticePlan {
  items: PlanItem[]
  note?: string
}
export interface FaceitLeague {
  competitionId: string
  league: string
  latest: number
}
export type EconConfidence = 'high' | 'medium' | 'low' | 'none'
export interface EconSplit {
  won: number
  played: number
  /** Null when nothing was played — 0% and "never happened" are different claims. */
  pct: number | null
  confidence: EconConfidence
}
export interface EconomyDepth {
  byType: Record<string, EconSplit>
  forceConversion: EconSplit
  afterLostEco: EconSplit
  afterLostFull: EconSplit
  savedAfterLoss: EconSplit
  rounds: number
}
export interface ScoutMapAgg {
  map: string
  demos: number
  wins: number
  losses: number
  roundsWon: number
  roundsPlayed: number
  roundWinPct: number
  opening: { CT: { k: number; d: number }; T: { k: number; d: number } }
  economy: Record<string, { w: number; p: number }>
  /** Absent on an aggregate cached by an older fold. */
  econDepth?: EconomyDepth
  util: { flashes: number; smokes: number; he: number; molly: number; blindTime: number; flashAssists: number }
  sites: { tRounds: number; plants: number; bySite: Record<string, number> }
  conv: {
    openWon: { n: number; won: number }
    openLost: { n: number; won: number }
    postPlant: { n: number; won: number }
    retake: { n: number; won: number }
  }
  reasons: Record<string, { w: number; l: number }>
  /** fx/fy = where it was thrown FROM. Absent on demos parsed before arcs were captured. */
  nades: { type: string; x: number; y: number; layer?: string | null; area?: string; fx?: number; fy?: number }[]
  paths: { round: number; site: string | null; pts: [number, number][]; layers?: (string | null)[] | null }[]
  setups: { round: number; pts: [number, number][]; layers?: (string | null)[] | null }[]
  toSite: { site: string; secs: number[] }[]
  spacing: { CT: { sum: number; n: number }; T: { sum: number; n: number } }
  spacingCurve?: { CT: Record<number, { sum: number; n: number }>; T: Record<number, { sum: number; n: number }> }
  plantSpots?: { x: number; y: number; n: number; layer?: string | null }[]
  players: ScoutPlayerAgg[]
}
export interface ScoutPlayerAgg {
  /** The fold's key — players rename themselves, so the display name never was one. */
  steamid64: string
  /** Their name in the most recent demo. */
  name: string
  /** What the roster calls them, when they're one of ours and an admin set it. */
  nick?: string
  /** Earlier nicknames, newest first. */
  aliases: string[]
  demos: number
  kills: number
  deaths: number
  assists: number
  adr: number
  rating: number
  kast: number
  hsPct: number
  openingKills: number
  openingCT: { k: number; d: number }
  openingT: { k: number; d: number }
  awpKills: number
  tradeKills: number
  tradedDeaths: number
  untradedDeaths: number
  multiKills: Record<string, number>
  acc: number
  util: {
    flashes: number
    smokes: number
    he: number
    molly: number
    flashAssists: number
    enemiesFlashed: number
    teamFlashed: number
    utilDmg: number
  }
  clutchW: number
  clutchP: number
  clutchBy: Record<string, { w: number; p: number }>
  topWeapon: string
}
export interface LineupRow {
  demoId: string
  map: string
  at: number
  sids: string[]
  names: string[]
  /** How many of this five also play in the newest demo in the folder. */
  overlap: number
}
export interface ScoutAggregate {
  name: string
  demoCount: number
  /** Per-demo lineups, oldest first — a folder is a set of games, not necessarily one team. */
  lineups?: LineupRow[]
  timeouts?: { called: number; brokeStreak: number; next3Won: number; next3Played: number; matches: number }
  maps: ScoutMapAgg[]
  players: ScoutPlayerAgg[]
}
export interface ScoutReport {
  overview: string
  maps: {
    map: string
    read: string
    tSide: string
    ctSide: string
    siteTendency: string
    keyPlayers: string
    howToBeat: string
  }[]
  players: { name: string; role: string; tendencies: string }[]
  keyTakeaways: string[]
  generatedAt?: string
  model?: string
}
export interface Scouting {
  aggregate: ScoutAggregate
  report: ScoutReport | null
  /** The written report predates the numbers beside it — demos were reparsed since it was made. */
  reportStale?: boolean
}

// --- Map veto (api/src/shared/veto.ts) ---
export interface VetoMapRecord {
  map: string
  played: number
  wins: number
  losses: number
  winPct: number
}
export interface VetoRow {
  map: string
  us: VetoMapRecord | null
  them: VetoMapRecord | null
  edge: number | null
  call: 'ban' | 'pick' | 'neutral' | 'thin'
  confidence: 'high' | 'medium' | 'low'
  why: string
}
export interface VetoSheet {
  opponent: string
  us: { name: string; matches: number; builtAt: string }
  them: { name: string; matches: number; builtAt: string }
  /** Which platforms their numbers came from. Optional: a sheet built by an older API has no such
   *  field, and the caption falls back to naming FACEIT alone, which is what those sheets were. */
  sources?: ('faceit' | 'league')[]
  rows: VetoRow[]
  banFirst: string[]
  pickFirst: string[]
  /** Games behind the rows shown — active-pool maps only, unlike `us.matches`/`them.matches`
   *  which count every competitive map ever played. Optional: a sheet cached by an older API
   *  won't have it. */
  sample?: { us: number; them: number }
  /** Where the vetoable-map list came from and how long since a human confirmed it. */
  pool?: { confirmed: string; ageDays: number; stale: boolean; source: string }
}

// --- Season (api/src/shared/season.ts) ---
export interface MissedTimeout {
  demoId: string
  map: string
  opponent: string
  from: number
  to: number
  length: number
  /** The slide ran to the last round — no timeout left worth spending, so not the same mistake. */
  toEndOfMatch: boolean
}
export interface SeasonTimeouts {
  called: number
  brokeStreak: number
  afterTimeoutWon: number
  afterTimeoutPlayed: number
  next3Won: number
  next3Played: number
  againstCalled: number
  againstBrokeStreak: number
  matches: number
  /** Absent on a season folded by an older API. */
  missed?: MissedTimeout[]
}
export interface SeasonMatch {
  demoId: string
  map: string
  league?: string
  finishedAt?: number
  us: number
  them: number
  opponent: string
}
export interface PlayerPoint {
  demoId: string
  map: string
  finishedAt?: number
  opponent: string
  rating: number
  adr: number
  kast: number
  kills: number
  deaths: number
}
export interface SeasonHeat {
  map: string
  /** [x, y, layer, ourSide] — ourSide is 'CT'/'T'/null. */
  ours: [number, number, string | null, (string | null)?][]
  theirs: [number, number, string | null, (string | null)?][]
}
export interface SelfReport {
  overview: string
  maps: { map: string; read: string }[]
  players: { name: string; note: string }[]
  strengths: string[]
  improve: string[]
  generatedAt?: string
  model?: string
}

export interface Change {
  label: string
  now: number
  before: number
  delta: number
  higherIsBetter: boolean
  unit: string
}
export interface Season {
  team: string
  leagues: string[]
  league: string | null
  matches: SeasonMatch[]
  aggregate: ScoutAggregate
  timeouts: SeasonTimeouts
  /** Coach-and-above ids present in the fold — excluded from "players this season". */
  staff?: string[]
  /** steamid64 → per-match form, oldest first. */
  trends: Record<string, PlayerPoint[]>
  /** What moved in the last match against everything before it. Absent under three matches. */
  changes?: Change[]
  heat: SeasonHeat[]
  report?: SelfReport | null
  builtAt: string
}

// --- AI match review (api/src/shared/insights.ts output) ---
export interface MatchInsights {
  summary: string
  strengths: string[]
  weaknesses: string[]
  improvements: string[]
  players: { steamid64: string; name: string; assessment: string; improve: string }[]
  generatedAt?: string
  model?: string
  /** The demo was re-parsed after this was written — it describes numbers that have since moved. */
  stale?: boolean
}

// --- Players (api/src/shared/players.ts) ---
export interface PlayerFeedback {
  demoId: string
  map: string
  opponent: string
  finishedAt?: number
  assessment: string
  improve: string
}
export interface RosterEntry {
  steamid64: string
  name: string
  nick?: string
  aliases: string[]
  demos: number
  rating: number
  adr: number
  kast: number
  recent: number[]
}
export interface RepeatedFix {
  line: string
  times: number
  demoIds: string[]
  lastAt?: number
}
export interface PlayerFile {
  steamid64: string
  name: string
  nick?: string
  aliases: string[]
  trends: PlayerPoint[]
  maps: { map: string; demos: number; rating: number; adr: number; kast: number; kills: number; deaths: number }[]
  totals: ScoutPlayerAgg | null
  feedback: PlayerFeedback[]
  /** The same advice given more than once — the point of the page. */
  repeated: RepeatedFix[]
}

// --- Roster / access (api/src/shared/roster.ts) ---
export interface Member {
  steamid64: string
  role: 'admin' | 'coach' | 'player'
  /** What an admin decided to call them — wins over the Steam persona everywhere. */
  nick?: string
  /** Steam persona, only present once they've signed in AND a Steam Web API key is configured. */
  name?: string
  addedAt: string
  addedBy?: string
  lastSeen?: string
  /** Set when an admin signed them out everywhere; sessions older than this are dead. */
  sessionsValidFrom?: string
}

// --- Review notes (api/src/shared/notes.ts) ---
export interface Note {
  id: string
  demoId: string
  /** The round it's about. Absent = the match as a whole. */
  round?: number
  body: string
  authorSid: string
  authorName: string
  /** SteamID64s named in it — they see it in their inbox. */
  mentions: string[]
  /** SteamID64s it's homework for. Coaches set these. */
  assignees: string[]
  /** Coaches and admins only. The API never sends these to a player, so its absence here is
   *  "public", not "unknown". */
  staffOnly?: boolean
  doneBy: string[]
  createdAt: string
  updatedAt?: string
}
export interface InboxItem {
  noteId: string
  demoId: string
  round?: number
  kind: 'assigned' | 'mentioned'
  body: string
  authorName: string
  createdAt: string
  done: boolean
  staffOnly?: boolean
}
/** Identity only — who can be @mentioned or assigned. */
export interface MemberLite {
  steamid64: string
  name: string
  role: 'admin' | 'coach' | 'player'
}

/** One destructive action, from api/src/shared/audit.ts. */
export interface AuditEntry {
  at: string
  actorSid: string
  actor: string
  action: string
  target: string
  detail?: string
}

// --- Round finder (api/src/shared/rounds.ts) ---
export interface RoundRow {
  demoId: string
  map: string
  round: number
  side: 'CT' | 'T'
  won: boolean
  buySelf: string
  buyOpp: string
  bombPlanted: boolean
  reason: string
  openTeam: 'self' | 'opp' | null
  openBy: string | null
  worstSelf: number
  worstOpp: number
  bestSelf: number
  bestOpp: number
  selfAliveMin: number
  opp: string
}
